import React from 'react';
import Pricing from '../components/sections/Pricing';

const Services: React.FC = () => {
  const services = [
    {
      icon: "/images/icons/brand.png",
      title: "Logo Design & Visual Identity",
      description: "We craft distinctive and memorable logos that capture the essence of your brand and leave a lasting impression. Our design process involves deep research into your industry, target audience, and brand values to create a visual identity that truly represents who you are.",
      features: [
        "Custom logo design",
        "Brand guidelines",
        "Business card design",
        "Letterhead & stationery",
        "Brand style guide"
      ],
      bgColor: "bg-brand-orange"
    },
    {
      icon: "/images/icons/rocket-lunch.png",
      title: "Social Media Marketing",
      description: "We create data-driven, engaging campaigns that connect with your audience and amplify your brand across digital platforms. Our social media strategies are tailored to your business goals and designed to drive real results.",
      features: [
        "Social media strategy",
        "Content creation",
        "Community management",
        "Paid advertising",
        "Analytics & reporting"
      ],
      bgColor: "bg-brand-blue"
    },
    {
      icon: "/images/icons/video-camera-alt.png",
      title: "Video Production",
      description: "Our team produces compelling video content—from concept to execution—that tells your story with clarity and emotion. We handle everything from pre-production planning to post-production editing.",
      features: [
        "Video strategy & planning",
        "Script writing",
        "Professional filming",
        "Post-production editing",
        "Motion graphics & animation"
      ],
      bgColor: "bg-brand-orange"
    },
    {
      icon: "/images/icons/calendar.png",
      title: "Event Branding",
      description: "We design immersive brand experiences for events, ensuring every touchpoint reflects your identity and leaves a powerful impact. From trade shows to corporate events, we create memorable experiences.",
      features: [
        "Event identity design",
        "Booth & display design",
        "Promotional materials",
        "Digital event assets",
        "On-site branding support"
      ],
      bgColor: "bg-brand-blue"
    },
    {
      icon: "/images/icons/website-design.png",
      title: "Web Design & Development",
      description: "We create stunning, responsive websites that not only look amazing but also deliver exceptional user experiences. From concept to launch, we build websites that drive results and engage your audience.",
      features: [
        "Responsive web design",
        "User experience (UX) design",
        "Frontend development",
        "Mobile optimization",
        "Performance optimization"
      ],
      bgColor: "bg-brand-orange"
    }
  ];

  return (
    <main className="pt-20 sm:pt-24">
      {/* Hero Section */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium text-gray-900 mb-4 sm:mb-6">
            Our Services
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Strategic design solutions for businesses, organizations, and startups that want to build strong, clear, and memorable brands
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <div key={index} className="group relative bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.bgColor === 'bg-brand-orange' ? 'from-orange-50 to-orange-100' : 'from-blue-50 to-blue-100'} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                
                {/* Card Content */}
                <div className="relative z-10 p-6 sm:p-8">
                  {/* Icon Section */}
                  <div className="text-center mb-4 sm:mb-6">
                    <div className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 ${service.bgColor} rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <img 
                        src={service.icon} 
                        alt={service.title}
                        className="w-8 h-8 sm:w-10 sm:h-10 md:w-14 md:h-14 object-contain"
                      />
                    </div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-medium text-gray-900 mb-2 sm:mb-3 group-hover:text-brand-blue transition-colors duration-300">
                      {service.title}
                    </h3>
                  </div>
                  
                  {/* Description */}
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6 text-center">
                    {service.description}
                  </p>
                  
                  {/* Features */}
                  <div className="mb-6 sm:mb-8">
                    <h4 className="font-medium text-gray-900 mb-3 sm:mb-4 text-center text-sm sm:text-base">What's Included:</h4>
                    <ul className="space-y-2 sm:space-y-3">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                          <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 ${service.bgColor === 'bg-brand-orange' ? 'bg-brand-orange' : 'bg-brand-blue'} rounded-full mr-2 sm:mr-3 flex-shrink-0`}></div>
                          <span className="text-xs sm:text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute top-4 right-4 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-white/20 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute bottom-4 left-4 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-tr from-white/10 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <Pricing />
    </main>
  );
};

export default Services;
